import React from 'react';
import { Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useUserProfileQuery } from '../../features/auth/authApi';
import { isAdmin, isTeacher } from '../../helpers/permissions';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import PersonTwoToneIcon from '@mui/icons-material/PersonTwoTone';
import SchoolTwoToneIcon from '@mui/icons-material/SchoolTwoTone';
import HandymanTwoToneIcon from '@mui/icons-material/HandymanTwoTone';
import MoveToInboxTwoToneIcon from '@mui/icons-material/MoveToInboxTwoTone';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
import GroupsTwoToneIcon from '@mui/icons-material/GroupsTwoTone';
import LibraryBooksTwoToneIcon from '@mui/icons-material/LibraryBooksTwoTone';

type MenuItem = Required<MenuProps>['items'][number];

const SideBarMenu = () => {
    const navigate = useNavigate();
    const { data: user } = useUserProfileQuery('userDetails');
    const { t } = useTranslation();

    const menuItemStyle = {
        fontSize: '1.5em',
        color: '#1890ff',
    };

    function getItem(
        label: React.ReactNode,
        key: React.Key,
        icon?: React.ReactNode,
        children?: MenuItem[],
        type?: 'group'
    ): MenuItem {
        return {
            key,
            icon,
            children,
            label,
            type,
        } as MenuItem;
    }
    const items: MenuProps['items'] = [];

    if (isTeacher(user)) {
        // Flat teacher menu: Inbox first (the teacher's landing page), then profile,
        // courses, and the (nested) workbench.
        items.push(
            getItem(
                t('sidebar.submissionInbox'),
                'SubmissionInbox',
                <MoveToInboxTwoToneIcon style={menuItemStyle} />
            ),
            getItem(
                t('sidebar.myProfile'),
                'Profile',
                <PersonTwoToneIcon style={menuItemStyle} />
            ),
            getItem(
                t('sidebar.courses'),
                'Courses',
                <SchoolTwoToneIcon style={menuItemStyle} />
            ),
            { type: 'divider' },
            // Authoring tools, flattened to top level under the divider.
            getItem(
                t('sidebar.workbench.courses'),
                'CourseDrafts',
                <HandymanTwoToneIcon style={menuItemStyle} />
            ),
            getItem(
                t('sidebar.workbench.lessons'),
                'Lessons',
                <MenuBookTwoToneIcon style={menuItemStyle} />
            )
        );
    } else {
        items.push(
            getItem(
                t('sidebar.myProfile'),
                'Profile',
                <PersonTwoToneIcon style={menuItemStyle} />
            ),
            getItem(
                t('sidebar.courses'),
                'Courses',
                <SchoolTwoToneIcon style={menuItemStyle} />
            )
        );
    }

    if (isAdmin(user)) {
        // Dedicated admin section, anchored under a divider at the bottom of
        // the menu. Visible only to admins (teachers never see these).
        items.push(
            { type: 'divider' },
            getItem(
                t('sidebar.admin.header'),
                'AdminGroup',
                null,
                [
                    getItem(
                        t('sidebar.admin.users'),
                        'AdminUsers',
                        <GroupsTwoToneIcon style={menuItemStyle} />
                    ),
                    getItem(
                        t('sidebar.admin.courses'),
                        'AdminCourses',
                        <LibraryBooksTwoToneIcon style={menuItemStyle} />
                    ),
                ],
                'group'
            )
        );
    }

    const onClick: MenuProps['onClick'] = (e) => {
        if (e.key === 'Profile') {
            navigate('/app/profile');
        } else if (e.key === 'Courses') {
            navigate('/app/courses');
        } else if (e.key === 'CourseDrafts') {
            navigate('/app/course-drafts');
        } else if (e.key === 'Lessons') {
            navigate('/app/course-components');
        } else if (e.key === 'SubmissionInbox') {
            navigate('/app/submission-inbox');
        } else if (e.key === 'AdminUsers') {
            navigate('/app/admin/users');
        } else if (e.key === 'AdminCourses') {
            navigate('/app/admin/courses');
        }
    };

    return (
        <Menu
            onClick={onClick}
            defaultSelectedKeys={['1']}
            defaultOpenKeys={['Profile']}
            mode="inline"
            items={items}
            theme="light"
        />
    );
};

export default SideBarMenu;
